<template>
  <div
    class="multi-menu d-flex align-center"
    :class="{ 'multi-menu--opened': menuOpened, 'multi-menu--mobile': isMobile }"
  >
    <ai-clear-button
      class="multi-menu__button d-flex align-center"
      :style="{
        'border-color': computedMenuList[sectionIndex].color,
        background: `rgb(from ${computedMenuList[sectionIndex].color} r g b / 0.1)`,
      }"
      @click="servicesList.length > 1 ? (menuOpened = !menuOpened) : null"
    >
      <div class="multi-menu__button__text" :style="{ color: computedMenuList[sectionIndex].color }">
        {{ computedMenuList[sectionIndex].title }}
      </div>
      <!-- <div class="multi-menu__button__arrow">
        <inline-svg
          :src="require('@/assets/icons/multi-menu/arrow.svg')"
          :style="{ color: menuList[sectionIndex].color }"
        />
      </div> -->
    </ai-clear-button>
    <v-spacer />
    <div class="plan" v-if="plan?.name === 'Пробный' && !isMobile">
      <div class="plan__text">{{ $t('subscriptions.to-plan-end') }}</div>
      <ai-countdown :time="time" @end="endHandler"/>
    </div>
    <div class="plan" v-else-if="plan?.name === 'Безлимитный на 30 дней' && !isMobile">
      <div class="plan__text">{{ $t('common.left') }}</div>
        {{ daysLeft }}
      <div class="plan__text">{{ $t('common.from') }}</div>
        {{ daysQn }}
      <span class="limits-data__text"> {{ $t('common.d') }}. </span>
      <span class="limits-data__divider"> / </span>
      <span class="limits-data__text"> {{ plan.name }} </span>
      <div class="plan__text">
        {{ $t('profile.plan-text') }}
      </div>
    </div>
    <v-spacer />
    <ai-clear-button v-if="servicesList.length > 1" @click="menuOpened = !menuOpened" class="all-services">
      <template v-if="!isMobile">
        {{ $t('multi-menu.all-services') }}
      </template>
      <inline-svg
        :src="require('@/assets/icons/multi-menu/all.svg')"
        :style="{ color: computedMenuList[sectionIndex].color }"
      />
    </ai-clear-button>

    <v-dialog
      :model-value="menuOpened"
      @update:model-value="menuOpened = false"
      :scrim="false"
      :content-class="{ 'multi-menu__menu': true, 'multi-menu__menu--mobile': isMobile }"
      attach=".app-template"
      absolute
    >
      <div class="multi-menu__menu__list" :class="{ 'multi-menu__menu__list--mobile': isMobile }">
        <ai-clear-button
          v-for="(item, index) in computedMenuList"
          :key="index"
          class="multi-menu__menu__list__item"
          @click="changeServiceHandler(item.service)"
        >
          <div class="multi-menu__menu__list__item__icon">
            <!-- <inline-svg :src="item.icon" /> -->
            <div class="multi-menu__menu__list__item__image">
              <img :src="item.image" alt="">
            </div>
          </div>
          <div class="multi-menu__menu__list__item__title">{{ item.title }}</div>
          <div class="multi-menu__menu__list__item__description">{{ item.description }}</div>
        </ai-clear-button>
      </div>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import moment from 'moment';

import { useAuthStore } from '@/store/auth';
import { useCommonStore } from '@/store/common';
import { useServicesStore } from '@/store/services';

import AiClearButton from '@/components/UI/AiClearButton/AiClearButton.vue';
import AiCountdown from '@/components/UI/AiCountdown/AiCountdown.vue';

import { IMultiMenuItem } from '@/types/IMultiMenuItem';

import { TService } from '@/types/TService';
import { useEditorStore } from '@/store/editor';

const { t } = useI18n();

const { generateImageData } = storeToRefs(useEditorStore());
const { currentService, isMobile, servicesList } = storeToRefs(useCommonStore());
const { imageResult, imageForActions, sourceBgImage, imagePrev } = storeToRefs(useServicesStore());
const { plan, isAvailable } = storeToRefs(useAuthStore());

const menuOpened = ref<boolean>(false);
const menuList = ref<IMultiMenuItem[]>([
  {
    title: t('multi-menu.market-title'),
    description: t('multi-menu.market-description'),
    color: 'var(--accent-100)',
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/market.svg'),
    image: `${process.env.VUE_APP_URL}/images/multi-menu/generation.png`,
    service: 'create_background_for_image',
  },
  {
    title: t('multi-menu.remove-background-title'),
    description: t('multi-menu.remove-background-description'),
    color: '#FF9EEA',
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/remove-background.svg'),
    image: `${process.env.VUE_APP_URL}/images/multi-menu/remove-background.png`,
    service: 'remove_background',
  },
  {
    title: t('multi-menu.infographics-title'),
    description: t('multi-menu.infographics-description'),
    color: '#86A1FF',
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/infographics.svg'),
    image: `${process.env.VUE_APP_URL}/images/multi-menu/infographics.png`,
    service: 'infographics',
  },
  {
    title: t('multi-menu.upscale-title'),
    description: t('multi-menu.upscale-description'),
    color: '#54FFC1',
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/upscale.svg'),
    image: `${process.env.VUE_APP_URL}/images/multi-menu/upscale.png`,
    service: 'upscale',
  },
  {
    title: t('multi-menu.outpaint-title'),
    description: t('multi-menu.outpaint-description'),
    color: '#FF9839',
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/outpaint.svg'),
    image: `${process.env.VUE_APP_URL}/images/multi-menu/outpaint.png`,
    service: 'outpaint',
  },
  {
    title: t('multi-menu.inpaint-title'),
    description: t('multi-menu.inpaint-description'),
    color: '#919191',
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/inpaint.svg'),
    image: `${process.env.VUE_APP_URL}/images/multi-menu/inpaint.png`,
    service: 'inpaint',
  },
  {
    title: t('multi-menu.shadow-title'),
    description: t('multi-menu.shadow-description'),
    color: '#FFF069',
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/shadow.svg'),
    image: `${process.env.VUE_APP_URL}/images/multi-menu/shadow.png`,
    service: 'shadow',
  },
  {
    title: t('multi-menu.watermarks-title'),
    description: t('multi-menu.watermarks-description'),
    color: '#D1A3FF',
    // eslint-disable-next-line global-require
    icon: require('@/assets/icons/multi-menu/watermarks.svg'),
    image: `${process.env.VUE_APP_URL}/images/multi-menu/watermarks.png`,
    service: 'watermarks',
  },
]);

const time = computed(() => {
  const currentDate = moment();
  const userExpDate = moment.utc(plan.value?.expiresAt).local();
  return userExpDate.diff(currentDate);
});

// eslint-disable-next-line max-len
const computedMenuList = computed(() => menuList.value.filter((menuItem: IMultiMenuItem) => servicesList.value.find((serviceItem: string) => menuItem.service === serviceItem)));
// eslint-disable-next-line max-len
const sectionIndex = computed(() => computedMenuList.value.findIndex((menuItem: IMultiMenuItem) => menuItem.service === currentService.value));

const daysLeft = computed(() => {
  const currentDate = moment();
  const expiresAt = moment.utc(plan.value?.expiresAt, 'YYYY-MM-DD HH:mm:ss');

  const diffInDays = expiresAt.diff(currentDate, 'days');
  return diffInDays + 1;
});

const daysQn = computed(() => {
  const connectedAt = moment.utc(plan.value?.connectedAt, 'YYYY-MM-DD HH:mm:ss');
  const expiresAt = moment.utc(plan.value?.expiresAt, 'YYYY-MM-DD HH:mm:ss');
  const diffInDays = expiresAt.diff(connectedAt, 'days');
  return diffInDays;
});

const changeServiceHandler = (service: TService): void => {
  menuOpened.value = false;
  currentService.value = service;
  // if (imageResult.value !== '') {
  sourceBgImage.value = '';
  imageForActions.value = '';
  imagePrev.value = '';
  imageResult.value = '';
  generateImageData.value.products = [];
  // }
};

const endHandler = (): void => {
  isAvailable.value = false;
};
</script>

<style scoped lang="scss" src="./MultiMenu.scss" />
